import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon, label, value, trend, color }) => (
  <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", display: "flex", alignItems: "center", gap: "1rem" }}>
    <div style={{ width: 48, height: 48, borderRadius: 10, background: `linear-gradient(135deg, ${color}20, ${color}10)`, display: "flex", alignItems: "center", justifyContent: "center", color, fontSize: "1.1rem" }}>
      <i className={`fas ${icon}`} />
    </div>
    <div style={{ flex: 1 }}>
      <p style={{ fontSize: ".75rem", color: "#9CA3AF", margin: 0, fontWeight: 500, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</p>
      <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1F2937", margin: ".1rem 0" }}>{value}</p>
      {trend && <p style={{ fontSize: ".75rem", color: trend.startsWith("+") ? "#10B981" : "#EF4444", margin: 0 }}>{trend}</p>}
    </div>
  </div>
);

export const AdminDashboard = ({ onNavigate }) => {
  const [contacts, setContacts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/contact`).then(r => r.data).catch(() => []),
      axios.get(`${API}/api/admin/blog`).then(r => r.data).catch(() => []),
      axios.get(`${API}/api/admin/social`).then(r => r.data).catch(() => []),
    ]).then(([c, b, s]) => {
      setContacts(c);
      setBlogPosts(b);
      setSocialPosts(s);
    });
  }, []);

  const publishedPosts = blogPosts.filter(p => p.published);

  return (
    <div>
      {/* Stats Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
        <StatCard icon="fa-eye" label="Blog Posts" value={blogPosts.length} trend={`${publishedPosts.length} published`} color="#EC4899" />
        <StatCard icon="fa-address-book" label="Contacts" value={contacts.length} trend={contacts.length > 0 ? `Latest: ${contacts[contacts.length - 1]?.name}` : "No contacts yet"} color="#8B5CF6" />
        <StatCard icon="fa-share-alt" label="Social Posts" value={socialPosts.length} color="#F97316" />
        <StatCard icon="fa-newspaper" label="Published" value={publishedPosts.length} trend={`${blogPosts.length - publishedPosts.length} drafts`} color="#10B981" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
        {/* Quick Actions */}
        <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1rem" }}>Quick Actions</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".6rem" }}>
            {[
              { label: "Edit Pages", icon: "fa-file-alt", id: "pages" },
              { label: "New Blog Post", icon: "fa-pen-fancy", id: "blog" },
              { label: "View Contacts", icon: "fa-address-book", id: "contacts" },
              { label: "Social Media", icon: "fa-share-alt", id: "social" },
              { label: "Media Library", icon: "fa-photo-video", id: "media" },
              { label: "Settings", icon: "fa-cog", id: "settings" },
            ].map(a => (
              <button key={a.id} onClick={() => onNavigate(a.id)} style={{
                display: "flex", alignItems: "center", gap: ".5rem", padding: ".65rem .8rem",
                background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8,
                cursor: "pointer", fontSize: ".82rem", fontWeight: 500, color: "#374151",
                fontFamily: "Inter, sans-serif", transition: "all .15s", textAlign: "left",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#EC4899"; e.currentTarget.style.color = "#EC4899"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#374151"; }}
              >
                <i className={`fas ${a.icon}`} style={{ width: 16, textAlign: "center", fontSize: ".75rem" }} />
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recent Contacts */}
        <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: 0 }}>Recent Contacts</h3>
            <button onClick={() => onNavigate("contacts")} style={{ background: "none", border: "none", color: "#EC4899", cursor: "pointer", fontSize: ".8rem", fontWeight: 600 }}>View All</button>
          </div>
          {contacts.length === 0 ? (
            <p style={{ color: "#9CA3AF", fontSize: ".85rem", textAlign: "center", padding: "2rem 0" }}>No contact submissions yet</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
              {contacts.slice(-5).reverse().map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: ".75rem", padding: ".55rem .7rem", background: "#F9FAFB", borderRadius: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #EC4899, #F97316)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: ".7rem", fontWeight: 700, flexShrink: 0 }}>
                    {c.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: ".82rem", fontWeight: 600, color: "#1F2937", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</p>
                    <p style={{ fontSize: ".72rem", color: "#9CA3AF", margin: 0 }}>{c.email}</p>
                  </div>
                  <span style={{ fontSize: ".68rem", color: "#9CA3AF", flexShrink: 0 }}>{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Blog Posts Overview */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginTop: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: 0 }}>Blog Posts</h3>
          <button onClick={() => onNavigate("blog")} style={{ background: "none", border: "none", color: "#EC4899", cursor: "pointer", fontSize: ".8rem", fontWeight: 600 }}>Manage</button>
        </div>
        {blogPosts.length === 0 ? (
          <p style={{ color: "#9CA3AF", fontSize: ".85rem", textAlign: "center", padding: "1.5rem 0" }}>No blog posts yet</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".82rem" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #E5E7EB" }}>
                  <th style={{ textAlign: "left", padding: ".6rem .5rem", color: "#6B7280", fontWeight: 600, fontSize: ".75rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Title</th>
                  <th style={{ textAlign: "left", padding: ".6rem .5rem", color: "#6B7280", fontWeight: 600, fontSize: ".75rem", textTransform: "uppercase" }}>Status</th>
                  <th style={{ textAlign: "left", padding: ".6rem .5rem", color: "#6B7280", fontWeight: 600, fontSize: ".75rem", textTransform: "uppercase" }}>Category</th>
                  <th style={{ textAlign: "left", padding: ".6rem .5rem", color: "#6B7280", fontWeight: 600, fontSize: ".75rem", textTransform: "uppercase" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {blogPosts.slice(0, 5).map(p => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{ padding: ".6rem .5rem", fontWeight: 500, color: "#1F2937" }}>{p.title}</td>
                    <td style={{ padding: ".6rem .5rem" }}>
                      <span style={{ padding: ".15rem .5rem", borderRadius: 50, fontSize: ".7rem", fontWeight: 600, background: p.published ? "rgba(16,185,129,.1)" : "rgba(107,114,128,.1)", color: p.published ? "#059669" : "#6B7280" }}>
                        {p.published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td style={{ padding: ".6rem .5rem", color: "#6B7280" }}>{p.category}</td>
                    <td style={{ padding: ".6rem .5rem", color: "#9CA3AF" }}>{new Date(p.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
